import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type {
  AdminQuestion,
  AdminUser,
  AuditEntry,
  Candidacy,
  CandidacyOrigin,
  CandidacyReview,
  GenericPoll,
  PollReview,
} from "@shared/types";
import { candidatePath } from "@shared/share-links";
import { api } from "../lib/api";
import { adminAuth } from "../lib/adminAuth";
import { Page, Card, ErrorBox } from "../components/ui";
import { ClaimBadge, OriginBadge, ReviewBadge } from "../components/StatusBadge";
import { MeasuresEditor, type DraftMeasure } from "../components/MeasuresEditor";
import { RichTextEditor } from "../components/RichTextEditor";
import { docOrLegacy } from "../lib/richdoc";

type AdminPoll = GenericPoll & { review: PollReview };

/** Stable order by added date (newest first) — a row doesn't jump when its
 * review/origin changes (e.g. approving a pending one). */
function byAddedDate(rows: Candidacy[]): Candidacy[] {
  return [...rows].sort((a, b) => b.createdAt - a.createdAt);
}

/** Unclaimed campaigns have no verified owner yet — drives the claim-token
 * affordance (issue a claim token vs reset an existing one). */
function isUnclaimed(c: Candidacy): boolean {
  return c.claimStatus === "unclaimed";
}

const TABS = ["candidatures", "users", "polls", "questions", "audit"] as const;
type AdminTab = (typeof TABS)[number];

/** Small reusable filter chip for the candidatures list. */
function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`text-xs px-2 py-0.5 rounded-full border ${
        active ? "border-accent text-accent bg-accent/10" : "border-border text-muted hover:text-accent"
      }`}
    >
      {children}
    </button>
  );
}

/** Reveal + copy block for a freshly minted token (admin relays it privately). */
function TokenReveal({ name, token }: { name: string; token: string }) {
  return (
    <div className="mt-3 text-sm bg-accent/10 border border-accent/25 rounded-lg p-3">
      <p className="text-muted">New token for <strong>{name}</strong> — send it to them privately:</p>
      <div className="flex items-center gap-2 mt-2">
        <code className="flex-1 bg-panel2 border border-border rounded px-2 py-1.5 text-xs break-all">{token}</code>
        <button
          onClick={() => navigator.clipboard?.writeText(token).catch(() => {})}
          className="bg-accent text-white rounded px-3 py-1.5 text-sm shrink-0"
        >
          Copy
        </button>
      </div>
    </div>
  );
}

export default function Admin() {
  // Local input value; the persisted credential lives in adminAuth.
  const [token, setToken] = useState(() => adminAuth.get());
  const [rows, setRows] = useState<Candidacy[] | null>(null);
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [polls, setPolls] = useState<AdminPoll[] | null>(null);
  const [questions, setQuestions] = useState<AdminQuestion[] | null>(null);
  const [audit, setAudit] = useState<AuditEntry[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [newTokens, setNewTokens] = useState<Record<string, string>>({});
  // Candidatures filters.
  const [reviewFilter, setReviewFilter] = useState<CandidacyReview | "all">("all");
  const [originFilter, setOriginFilter] = useState<CandidacyOrigin | "all">("all");
  // Tab lives in the URL (?tab=users) so refresh + back/forward preserve it.
  const [params, setParams] = useSearchParams();
  const raw = params.get("tab");
  const tab: AdminTab = (TABS as readonly string[]).includes(raw ?? "") ? (raw as AdminTab) : "candidatures";
  const setTab = (t: AdminTab) => setParams(t === "candidatures" ? {} : { tab: t });

  async function load(t: string) {
    setBusy(true);
    setErr(null);
    adminAuth.set(t); // persist before the calls — the api reads it from here
    try {
      const [c, u, p, q, log] = await Promise.all([
        api.adminCandidacies(),
        api.adminUsers(),
        api.adminPolls(),
        api.adminQuestions(),
        api.adminAudit(),
      ]);
      setRows(c);
      setUsers(u);
      setPolls(p);
      setQuestions(q);
      setAudit(log);
    } catch (e) {
      setErr((e as Error).message);
      // Clear everything together — a partial view mixing fresh and stale
      // datasets is worse than an empty one.
      setRows(null);
      setUsers(null);
      setPolls(null);
      setQuestions(null);
      setAudit(null);
    } finally {
      setBusy(false);
    }
  }

  // Auto-load on mount when a token was saved in a previous session, so the
  // admin lands straight on the queue instead of re-clicking "Load".
  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function update(id: string, patch: Partial<Pick<Candidacy, "review" | "origin">>) {
    try {
      await api.adminUpdate(id, patch);
      setRows((cur) => cur?.map((r) => (r.id === id ? { ...r, ...patch } : r)) ?? null);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  async function resetToken(id: string, username: string, claim = false) {
    const msg = claim
      ? `Issue a claim token for ${username} (marks the user verified if the campaign has a linked WarEra account)?`
      : `Reset ${username}'s management token? Their old token stops working immediately.`;
    if (!confirm(msg)) return;
    try {
      const { editToken } = await api.adminResetToken(id);
      setNewTokens((cur) => ({ ...cur, [id]: editToken }));
      if (claim) {
        await update(id, { review: "published" });
        if (token) await load(token);
      }
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  function onEdited(id: string, patch: Partial<Candidacy>) {
    setRows((cur) => cur?.map((r) => (r.id === id ? { ...r, ...patch } : r)) ?? null);
  }

  async function verifyUser(u: AdminUser) {
    const msg =
      u.status === "claimed"
        ? `Reset ${u.username}'s management token? Their old token stops working immediately.`
        : `Verify ${u.username} and issue them a management token?`;
    if (!confirm(msg)) return;
    try {
      const { editToken } = await api.adminVerifyUser(u.id);
      setNewTokens((cur) => ({ ...cur, [`u:${u.id}`]: editToken }));
      if (token) await load(token); // refresh derived status everywhere
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  async function unverifyUser(u: AdminUser) {
    if (!confirm(`Unverify ${u.username}? Their token stops working and all their candidacies revert to unverified.`)) return;
    try {
      await api.adminUnverifyUser(u.id);
      if (token) await load(token);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  async function setPoll(id: string, review: PollReview) {
    try {
      await api.adminSetPollReview(id, review);
      setPolls((cur) => cur?.map((p) => (p.id === id ? { ...p, review } : p)) ?? null);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  async function setQuestionHidden(id: string, hidden: boolean) {
    try {
      await api.adminHideQuestion(id, hidden);
      setQuestions((cur) => cur?.map((q) => (q.id === id ? { ...q, hidden } : q)) ?? null);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  const pendingCount = (rows ?? []).filter((r) => r.review === "pending").length;
  const loaded = rows !== null;
  const candidatures = byAddedDate(
    (rows ?? []).filter(
      (r) => (reviewFilter === "all" || r.review === reviewFilter) && (originFilter === "all" || r.origin === originFilter),
    ),
  );

  const counts: Record<AdminTab, number> = {
    candidatures: rows?.length ?? 0,
    users: users?.length ?? 0,
    polls: polls?.length ?? 0,
    questions: questions?.length ?? 0,
    audit: audit?.length ?? 0,
  };
  const labels: Record<AdminTab, string> = {
    candidatures: `Candidatures${pendingCount ? ` · ${pendingCount} pending` : ""}`,
    users: "Users",
    polls: "Polls",
    questions: "Questions",
    audit: "Audit",
  };

  return (
    <Page>
      <h1 className="text-xl font-semibold my-3">Admin</h1>

      <Card className="mb-4">
        <label className="block text-sm mb-1 text-muted">Admin token</label>
        <div className="flex gap-2">
          <input
            type="password"
            className="flex-1 bg-panel2 border border-border rounded px-2 py-1.5 text-sm"
            value={token}
            onChange={(e) => setToken(e.target.value)}
          />
          <button
            disabled={!token || busy}
            onClick={() => load(token)}
            className="bg-accent text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50"
          >
            {busy ? "Loading…" : "Load"}
          </button>
        </div>
        {err && <div className="mt-3"><ErrorBox msg={err} /></div>}
      </Card>

      {loaded && (
        <div className="flex gap-1 mb-3 border-b border-border overflow-x-auto">
          {TABS.map((key) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`px-3 py-2 text-sm font-medium -mb-px border-b-2 whitespace-nowrap ${
                tab === key ? "border-accent text-accent" : "border-transparent text-muted hover:text-accent"
              }`}
            >
              {labels[key]} <span className="text-muted">({counts[key]})</span>
            </button>
          ))}
        </div>
      )}

      {/* --- Candidatures --- */}
      {loaded && tab === "candidatures" && (
        <>
          <div className="flex flex-wrap gap-3 mb-3 text-xs items-center">
            <span className="text-muted">Status:</span>
            <Chip active={reviewFilter === "all"} onClick={() => setReviewFilter("all")}>all</Chip>
            <Chip active={reviewFilter === "pending"} onClick={() => setReviewFilter("pending")}>pending</Chip>
            <Chip active={reviewFilter === "published"} onClick={() => setReviewFilter("published")}>published</Chip>
            <Chip active={reviewFilter === "hidden"} onClick={() => setReviewFilter("hidden")}>hidden</Chip>
            <span className="text-muted ml-2">Origin:</span>
            <Chip active={originFilter === "all"} onClick={() => setOriginFilter("all")}>all</Chip>
            <Chip active={originFilter === "imported"} onClick={() => setOriginFilter("imported")}>imported</Chip>
            <Chip active={originFilter === "declared"} onClick={() => setOriginFilter("declared")}>declared</Chip>
          </div>
          <div className="space-y-3">
            {candidatures.length === 0 && <p className="text-muted text-sm">No candidacies match these filters.</p>}
            {candidatures.map((r) => (
              <AdminRow
                key={r.id}
                row={r}
                newToken={newTokens[r.id]}
                onUpdate={update}
                onReset={resetToken}
                onEdited={onEdited}
                onError={setErr}
              />
            ))}
          </div>
        </>
      )}

      {/* --- Users --- */}
      {loaded && tab === "users" && (
        <div className="space-y-3">
          {(users ?? []).length === 0 && <p className="text-muted text-sm">No candidate users yet.</p>}
          {(users ?? []).map((u) => (
            <Card key={u.id}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold">
                    <Link to={candidatePath(u.username)} className="hover:underline">{u.username}</Link>
                  </div>
                  <div className="text-xs text-muted">
                    {u.candidacyCount} candidac{u.candidacyCount === 1 ? "y" : "ies"} · added {new Date(u.createdAt).toLocaleDateString()}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {u.status === "claimed" ? (
                    <ClaimBadge claimStatus="claimed" />
                  ) : (
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-muted/15 text-muted">unverified</span>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-3 mt-3 text-sm">
                {u.status === "claimed" ? (
                  <button onClick={() => unverifyUser(u)} className="text-warn font-semibold">Unverify</button>
                ) : (
                  <button onClick={() => verifyUser(u)} className="text-green-300 font-semibold">Verify (issue token)</button>
                )}
                {u.status === "claimed" && (
                  <button onClick={() => verifyUser(u)} className="text-accent">Reset token</button>
                )}
                {u.status === "claimed" && !u.hasToken && (
                  <span className="text-xs text-warn">claimed but tokenless</span>
                )}
              </div>
              {newTokens[`u:${u.id}`] && <TokenReveal name={u.username} token={newTokens[`u:${u.id}`]} />}
            </Card>
          ))}
        </div>
      )}

      {/* --- Polls --- */}
      {loaded && tab === "polls" && (
        <div className="space-y-3">
          {(polls ?? []).length === 0 && <p className="text-muted text-sm">No community polls yet.</p>}
          {(polls ?? []).map((p) => (
            <Card key={p.id}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold break-words">{p.question}</div>
                  <div className="text-xs text-muted mt-0.5">
                    {p.countryId ?? "global"}{p.tag ? ` · ${p.tag}` : ""} · {p.total} vote{p.total === 1 ? "" : "s"} · {new Date(p.createdAt).toLocaleDateString()}
                  </div>
                  <div className="text-xs text-muted mt-1">{p.options.map((o) => o.label).join(" · ")}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <ReviewBadge review={p.review} />
                </div>
              </div>
              <div className="flex gap-3 mt-3 text-sm">
                {p.review === "published" ? (
                  <button onClick={() => setPoll(p.id, "hidden")} className="text-warn">Hide</button>
                ) : (
                  <button onClick={() => setPoll(p.id, "published")} className="text-green-300">Unhide</button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* --- Questions --- */}
      {loaded && tab === "questions" && (
        <div className="space-y-3">
          {(questions ?? []).length === 0 && <p className="text-muted text-sm">No AMA questions yet.</p>}
          {(questions ?? []).map((q) => (
            <Card key={q.id}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="break-words">{q.body}</div>
                  <div className="text-xs text-muted mt-1">
                    asked {q.askerName ? `by ${q.askerName}` : "anonymously"} · {q.upvotes} upvote{q.upvotes === 1 ? "" : "s"} · {new Date(q.createdAt).toLocaleDateString()}
                  </div>
                  <Link
                    to={`/c/${q.countryId}/race/${q.office}/${q.candidacyId}`}
                    className="text-accent text-xs"
                  >
                    {q.candidacyUsername} ({q.office}) →
                  </Link>
                  {q.answer && (
                    <div className="text-sm mt-2 border-l-2 border-border pl-2 text-muted break-words">{q.answer}</div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {q.hidden && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-warn/15 text-warn">hidden</span>}
                </div>
              </div>
              <div className="flex gap-3 mt-3 text-sm">
                {q.hidden ? (
                  <button onClick={() => setQuestionHidden(q.id, false)} className="text-green-300">Unhide</button>
                ) : (
                  <button onClick={() => setQuestionHidden(q.id, true)} className="text-warn">Hide</button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* --- Audit --- */}
      {loaded && tab === "audit" && (
        <div>
          <p className="text-xs text-muted mb-3">
            Rejected admin attempts and successful admin actions. A burst of rejected attempts from an
            unfamiliar IP means someone is probing the admin token.
          </p>
          {(audit ?? []).length === 0 ? (
            <p className="text-muted text-sm">No activity logged yet.</p>
          ) : (
            <Card className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-muted text-left">
                  <tr>
                    <th className="py-1 pr-3 font-medium">When</th>
                    <th className="py-1 pr-3 font-medium">Result</th>
                    <th className="py-1 pr-3 font-medium">IP</th>
                    <th className="py-1 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(audit ?? []).map((e) => (
                    <tr key={e.id} className="border-t border-border">
                      <td className="py-1 pr-3 whitespace-nowrap">{new Date(e.at).toLocaleString()}</td>
                      <td className={`py-1 pr-3 font-semibold ${e.ok ? "text-green-300" : "text-warn"}`}>
                        {e.ok ? "ok" : "REJECTED"}
                      </td>
                      <td className="py-1 pr-3 whitespace-nowrap">{e.ip ?? "—"}</td>
                      <td className="py-1 break-all">{e.action}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </div>
      )}
    </Page>
  );
}

function AdminRow({
  row,
  newToken,
  onUpdate,
  onReset,
  onEdited,
  onError,
}: {
  row: Candidacy;
  newToken?: string;
  onUpdate: (id: string, patch: Partial<Pick<Candidacy, "review" | "origin">>) => void;
  onReset: (id: string, username: string, claim?: boolean) => void;
  onEdited: (id: string, patch: Partial<Candidacy>) => void;
  onError: (msg: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [introDoc, setIntroDoc] = useState(() => docOrLegacy(row.introDoc, row.intro));
  const [party, setParty] = useState(row.party ?? "");
  const [measures, setMeasures] = useState<DraftMeasure[]>(
    row.measures.map((m) => ({ title: m.title, detailDoc: docOrLegacy(m.detailDoc, m.detail) })),
  );
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      const next = await api.adminUpdatePlatform(row.id, {
        introDoc,
        party: party.trim() || undefined,
        measures: measures.map((m) => ({ title: m.title, detailDoc: m.detailDoc })),
      });
      onEdited(row.id, { intro: next.intro, introDoc: next.introDoc, party: next.party, measures: next.measures });
      setEditing(false);
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <div className="font-semibold">
            <Link to={candidatePath(row.username)} className="hover:underline">{row.username}</Link>
          </div>
          <div className="text-xs text-muted">
            {row.office} · {row.countryId} · {new Date(row.cycleStartAt).toLocaleDateString()}
          </div>
          <div className="flex gap-3 mt-1 text-xs">
            <Link to={`/c/${row.countryId}/race/${row.office}/${row.id}`} className="text-accent">Campaign page →</Link>
            <Link to={`/c/${row.countryId}`} className="text-accent">Country →</Link>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <ReviewBadge review={row.review} />
          <ClaimBadge claimStatus={row.claimStatus} />
          <OriginBadge origin={row.origin} />
        </div>
      </div>

      {/* Review (visibility) controls */}
      <div className="flex flex-wrap gap-2 mt-3 text-sm">
        {row.review === "pending" && (
          <>
            <button onClick={() => onUpdate(row.id, { review: "published" })} className="text-green-300 font-semibold">Approve ✓</button>
            <button onClick={() => onUpdate(row.id, { review: "hidden" })} className="text-warn font-semibold">Reject ✗</button>
          </>
        )}
        {row.review === "published" && (
          <button onClick={() => onUpdate(row.id, { review: "hidden" })} className="text-warn">Hide</button>
        )}
        {row.review === "hidden" && (
          <button onClick={() => onUpdate(row.id, { review: "published" })} className="text-green-300">Unhide</button>
        )}
        <button onClick={() => setEditing((v) => !v)} className="text-accent">{editing ? "Close editor" : "Edit"}</button>
        <button onClick={() => onReset(row.id, row.username, isUnclaimed(row))} className="text-warn">
          {isUnclaimed(row) ? "Issue claim token" : "Reset token"}
        </button>
      </div>

      {/* Origin (provenance) toggle — imported ⇄ declared. Trust is separate (claim). */}
      <div className="flex flex-wrap gap-2 mt-2 text-xs text-muted items-center">
        <span>Provenance:</span>
        {row.origin !== "imported" && (
          <button onClick={() => onUpdate(row.id, { origin: "imported" })} className="text-purple-300">Set imported</button>
        )}
        {row.origin !== "declared" && (
          <button onClick={() => onUpdate(row.id, { origin: "declared" })} className="hover:text-accent">Set declared</button>
        )}
      </div>

      {editing && (
        <div className="mt-3 border-t border-border pt-3">
          <label className="block text-sm mb-1 text-muted">Party</label>
          <input
            className="w-full bg-panel2 border border-border rounded px-2 py-1.5 text-sm mb-3"
            maxLength={60}
            value={party}
            onChange={(e) => setParty(e.target.value)}
            placeholder="leave blank for independent"
          />
          <label className="block text-sm mb-1 text-muted">Manifesto</label>
          <div className="mb-3">
            <RichTextEditor valueDoc={introDoc} onChange={setIntroDoc} ariaLabel="Manifesto" />
          </div>
          <label className="block text-sm mb-2 text-muted">Measures</label>
          <MeasuresEditor value={measures} onChange={setMeasures} />
          <div className="flex gap-2 mt-3">
            <button disabled={busy} onClick={save} className="bg-accent text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50">
              {busy ? "Saving…" : "Save"}
            </button>
            <button onClick={() => setEditing(false)} className="text-muted text-sm px-3">Cancel</button>
          </div>
        </div>
      )}

      {newToken && <TokenReveal name={row.username} token={newToken} />}
    </Card>
  );
}
